import {useCallback, useState} from 'react';

/**
 * Manages simple boolean open/close state for modals and dialogs.
 *
 * @example
 * const createModal = useDisclosure();
 * <Button onClick={createModal.open}>Open</Button>
 * <Modal open={createModal.isOpen} onOpenChange={createModal.toggle}>...</Modal>
 */
export function useDisclosure(initialOpen = false) {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  return {isOpen, open, close, toggle};
}

/**
 * Manages open/close state for modals that carry a data payload (e.g. edit/delete).
 * The modal is considered open when `data` is non-null.
 *
 * @example
 * const editModal = useDisclosureWithData<TrustAccountListItemDTO>();
 * <Button onClick={() => editModal.open(account)}>Edit</Button>
 * <EditModal
 *   account={editModal.data}
 *   open={editModal.isOpen}
 *   onClose={editModal.close}
 * />
 */
export function useDisclosureWithData<T>() {
  const [data, setData] = useState<T | null>(null);

  const isOpen = data !== null;
  const open = useCallback((payload: T) => setData(payload), []);
  const close = useCallback(() => setData(null), []);

  return {isOpen, data, open, close};
}
